import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Blocks, Briefcase, Car, ChevronDown, ChevronsDown, Clapperboard, BookOpen, Brain, Gamepad2, GraduationCap, HeartPulse, Headphones, Languages, Lightbulb, MailQuestion, MonitorCog, Scale, Search, Smartphone, Sparkles, TrendingUp, Zap, Trophy, UserSearch } from "lucide-react";
import { api } from "../api";
import { ProductCard } from "../components/ProductCard";
import { ProductGrid } from "../components/ProductGrid";
import { BookCover } from "../components/BookCover";
import { Reveal } from "../components/Reveal";
import { StatCounter } from "../components/StatCounter";
import { WaveformPlayer } from "../components/WaveformPlayer";
import { QuizSection } from "../components/QuizSection";
import { AppsSection } from "../components/AppsSection";
import { GamesSection, RaceSection, BundleCard, SUBJECTS } from "../components/GamesSection";
import { APP_CATEGORIES, AVAILABLE_APPS } from "../data/apps";
import { PricingCampaign } from "../components/PricingCampaign";
import { BannerCarousel } from "../components/BannerCarousel";
import { PromoStrip } from "../components/PromoStrip";
import { PromoVideo } from "../components/PromoVideo";
import { TutorCampaign } from "../components/TutorCampaign";
import { Testimonials } from "../components/Testimonials";
import { NewsletterSection } from "../components/NewsletterSection";
import { HomeLibraryStrip } from "../components/HomeLibraryStrip";
import { useLanguage } from "../context/LanguageContext";
import { useStaticSeo, ORGANIZATION, SITE, SITE_NAME } from "../lib/seo";

const ICONS = { BookOpen, Search, Sparkles, TrendingUp, Brain, Lightbulb, Languages, Blocks, GraduationCap };
const APP_CAT_ICONS = { edukacja: GraduationCap, motoryzacja: Car, zdrowie: HeartPulse, firma: Briefcase, prawo: Scale, technologia: MonitorCog, multimedia: Clapperboard, sport: Trophy, kariera: UserSearch };

const CAT_COLORS = {
  "beletrystyka": "#22d3ee", "kryminal-i-thriller": "#fb7185", "sci-fi-i-fantasy": "#c084fc",
  "biznes-i-ekonomia": "#fbbf24", "rozwoj-osobisty": "#34d399", "poradniki": "#a3e635",
  "nauka-jezykow": "#f0abfc", "edukacja-dzieci": "#60a5fa", "korepetytor-ai": "#2dd4bf",
};

const FLOAT_GAME = { id: "float-game", title: "DigiLib World", author: "Gra edukacyjna 3D", cover_image: "/img/covers/digilib-world-geografia.jpeg" };
const FLOAT_SLOTS = [
  { id: "cien-nad-wisla", pos: { top: 18, left: "4%" } },
  { id: "sila-malych-krokow", pos: { top: 55, right: "6%" } },
  { id: "kroniki-rozbitego-nieba", pos: { top: 30, right: "20%" } },
  { id: "float-game", pos: { top: 60, left: "15%" } },
  { id: "digilingua-hiszpanski", pos: { top: 10, right: "3%" } },
];
const SUN_FLARES = [
  { l: "44%", t: "13%", s: 34, c: "rgba(255,220,150,0.38)", d: 0 },
  { l: "38%", t: "24%", s: 64, c: "rgba(120,240,255,0.30)", d: 1.2 },
  { l: "31%", t: "37%", s: 26, c: "rgba(255,170,230,0.42)", d: 2.1 },
  { l: "24%", t: "52%", s: 104, c: "rgba(255,230,170,0.22)", d: 0.6 },
  { l: "57%", t: "19%", s: 22, c: "rgba(200,255,255,0.4)", d: 1.7 },
];
const SUN_SPARKS = [[12, 20], [22, 8], [78, 14], [88, 30], [68, 42], [30, 66], [8, 58], [92, 64], [55, 30], [45, 52], [18, 38], [72, 70]];

export default function Home() {
  useStaticSeo("/", { jsonLd: [ORGANIZATION, { "@type": "WebSite", "@id": `${SITE}/#website`, url: SITE, name: SITE_NAME, inLanguage: "pl-PL", publisher: { "@id": `${SITE}/#organization` } }] });
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [facts, setFacts] = useState(null); // prawdziwe liczby o ofercie, liczone z bazy
  const [active, setActive] = useState(() => new URLSearchParams(window.location.search).get("kat") || "wszystkie"); // ?kat= z menu na innej podstronie
  const [appGamesHub, setAppGamesHub] = useState(null); // null | "aplikacje" | "gry" - panel podkategorii dla kafli ktore nie filtruja siatki na tej stronie, tylko przewijaja do wlasnej sekcji
  const heroRef = useRef(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/store/facts").then((r) => setFacts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e) => setActive(e.detail);
    window.addEventListener("set-category-filter", h);
    return () => window.removeEventListener("set-category-filter", h);
  }, []);

  const filtered = active === "wszystkie"
    ? products.filter((p) => p.format !== "aplikacja" && !p.id.startsWith("pakiet-")) // katalog: ebooki/audiobooki, pakiety pod siatką
    : active.startsWith("fmtcat:")
      ? (() => { const [, fmt, cat] = active.split(":"); return products.filter((p) => p.format === fmt && p.category === cat); })()
      : active.startsWith("format:")
        ? products.filter((p) => p.format === active.slice(7) && !p.id.startsWith("pakiet-"))
        : active.startsWith("subcat:")
          ? products.filter((p) => p.subcategory === active.slice(7))
          : products.filter((p) => p.category === active);

  // kategorie ksiazkowe (nie app-owe) faktycznie wystepujace w danym formacie - zero "martwych" filtrow
  const bookCategoriesFor = (fmt) => {
    const present = new Set(products.filter((p) => p.format === fmt && p.category !== "pakiety").map((p) => p.category));
    return categories.filter((c) => present.has(c.slug));
  };
  const activeFormat = active === "wszystkie" ? null : active.startsWith("format:") ? active.slice(7)
    : active.startsWith("fmtcat:") ? active.split(":")[1] : null;
  const apps = products.filter((p) => p.format === "aplikacja");
  const games = products.filter((p) => p.subcategory === "gry-edukacyjne");
  const bundleEbooki = products.find((p) => p.id === "pakiet-ebooki");
  const bundleAudio = products.find((p) => p.id === "pakiet-audiobooki");
  const bundleCombo = products.find((p) => p.id === "pakiet-ebook-audio");
  const nEbooks = products.filter((p) => p.format === "ebook" && !p.id.startsWith("pakiet-")).length;
  const nAudio = products.filter((p) => p.format === "audiobook" && !p.id.startsWith("pakiet-")).length;
  const fmtPrice = (v) => v.toFixed(2).replace(".", ",").replace(",00", "");
  const bestsellers = products.filter((p) => p.badge === "bestseller");
  // link z kotwicą (np. /#bestsellery ze stopki): przewijamy dopiero po wczytaniu produktów,
  // bo wcześniej sekcje są puste i przeglądarka zostaje na górze strony
  useEffect(() => {
    const id = decodeURIComponent((window.location.hash || "").slice(1));
    if (!id || !products.length) return undefined;
    const go = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    const timers = [150, 700, 1500].map((ms) => setTimeout(go, ms)); // powtórka po doładowaniu obrazków
    return () => timers.forEach(clearTimeout);
  }, [products.length]);
  const lektury = products.filter((p) => p.category === "lektury-szkolne");
  const audiobooks = products.filter((p) => p.format === "audiobook").slice(0, 3);
  const floating = FLOAT_SLOTS
    .map((slot) => ({ p: slot.id === "float-game" ? FLOAT_GAME : products.find((x) => x.id === slot.id), pos: slot.pos }))
    .filter((f) => f.p);

  const onHeroMove = (e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    setParallax({ x: (e.clientX - rect.width / 2) / rect.width, y: (e.clientY - rect.top - rect.height / 2) / rect.height });
  };
  // Animacje blasku działają tylko wtedy, gdy baner jest na ekranie — poza nim są wstrzymane (płynne przewijanie)
  const [heroVisible, setHeroVisible] = useState(true);
  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(([entry]) => setHeroVisible(entry.isIntersecting), { threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  // Telefon: blask słońca podąża za palcem (przewijanie strony działa normalnie — niczego nie blokujemy)…
  const onHeroTouch = (e) => {
    const t = e.touches[0];
    if (t) onHeroMove(t);
  };
  // …oraz za przechyleniem telefonu, tam gdzie przeglądarka udostępnia czujnik bez pytania o zgodę (Android).
  // Na iPhonie czujnik wymaga osobnej zgody — nie wyświetlamy takiego okna, zostaje sterowanie palcem.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia("(pointer: coarse)").matches) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (typeof window.DeviceOrientationEvent === "undefined" || typeof window.DeviceOrientationEvent.requestPermission === "function") return undefined;
    let frame = 0;
    let base = null; // kąt, pod jakim użytkownik trzyma telefon — ruch liczymy względem niego
    const clamp = (v) => Math.max(-0.5, Math.min(0.5, v));
    const onTilt = (e) => {
      if (e.gamma == null || e.beta == null || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (window.scrollY > window.innerHeight) return; // baner poza ekranem — nie przerysowujemy
        if (base === null) base = e.beta;
        setParallax({ x: clamp(e.gamma / 50), y: clamp((e.beta - base) / 50) });
      });
    };
    window.addEventListener("deviceorientation", onTilt, { passive: true });
    return () => { window.removeEventListener("deviceorientation", onTilt); cancelAnimationFrame(frame); };
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} onMouseMove={onHeroMove} onTouchStart={onHeroTouch} onTouchMove={onHeroTouch} className="grain hero-mist relative flex min-h-[84vh] items-center overflow-hidden bg-slate-950 pb-24 pt-28 sm:pt-32" data-testid="hero-section">
        <img
          src="/img/hero/hero-bg.jpg"
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{
            transform: `translate(${parallax.x * -18}px, ${parallax.y * -12}px) scale(1.06)`,
            transition: "transform 0.3s ease-out",
          }}
        />
        <div className="pointer-events-none absolute inset-0 hero-fade" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 55% at 50% 0%, rgba(34,211,238,0.28), transparent 65%), radial-gradient(ellipse 60% 50% at 80% 110%, rgba(20,184,166,0.15), transparent 60%)" }} />
        {/* ŻYWY BLASK SŁOŃCA */}
        <div className={`sun-fx ${heroVisible ? "" : "sun-paused"}`} aria-hidden="true" data-testid="hero-sun">
          <div className="sun-move" style={{ position: "absolute", inset: 0, "--sun-x": parallax.x, "--sun-y": parallax.y, transform: `translate(${parallax.x * -22}px, ${parallax.y * -12}px)`, transition: "transform 0.35s ease-out" }}>
            <div className="sun-core" />
            <div className="sun-core-gold" />
            <div className="sun-rays sun-rays-a" />
            <div className="sun-rays sun-rays-b" />
            <div className="sun-glint" />
            {SUN_FLARES.map((f, i) => (
              <div key={i} className="sun-flare" style={{ left: f.l, top: f.t, width: f.s, height: f.s, background: `radial-gradient(circle, ${f.c}, transparent 70%)`, animationDelay: `${f.d}s` }} />
            ))}
            <div className="hero-horizon" />
          </div>
          {SUN_SPARKS.map(([x, y], i) => (
            <div key={i} className="sun-spark" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.45}s`, transform: "translate(-50%, -50%)" }} />
          ))}
        </div>

        <div className="pointer-events-none absolute inset-0 hero-text-scrim" />
        <div className="pointer-events-none absolute inset-x-0 top-0 hero-nav-scrim" />
        {/* mgła na dole bannera — zamglone przejście zamiast prostej linii */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-52 backdrop-blur-2xl hero-mist-band"
          style={{
            maskImage: "linear-gradient(to bottom, transparent, #000 55%)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent, #000 55%)",
          }}
        />

        {floating.map(({ p, pos }, i) => (
          <motion.div
            key={p.id}
            className="pointer-events-none absolute z-0 hidden w-28 opacity-75 lg:block xl:w-36"
            style={{
              top: `${pos.top}%`,
              ...(pos.left ? { left: pos.left } : { right: pos.right }),
              transform: `translate(${parallax.x * (12 + i * 8)}px, ${parallax.y * (10 + i * 6)}px)`,
            }}
            animate={{ y: [-14, 14], rotate: [-4, 4] }}
            transition={{ duration: 5 + i * 1.3, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
          >
            <BookCover product={p} />
          </motion.div>
        ))}

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-2 rounded-full glass-panel px-4 py-1.5 text-xs font-semibold text-cyan-300">
              <Zap className="h-3.5 w-3.5" /> {t("home.hero.badge")}
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 font-display text-4xl font-extrabold tracking-tight [filter:drop-shadow(0_2px_18px_rgba(2,16,40,0.55))] sm:text-5xl lg:text-6xl"
          >
            {t("home.hero.title.line1")}<br />
            <span className="gradient-text">{t("home.hero.title.line2")}</span><br />
            {t("home.hero.title.line3")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mx-auto mt-6 max-w-xl text-base text-slate-100 [text-shadow:0_1px_14px_rgba(2,16,40,0.9)] sm:text-lg"
          >
            {t("home.hero.subtitle")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-cta-catalog"
              onClick={() => document.getElementById("katalog")?.scrollIntoView({ behavior: "smooth" })}
              className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 px-8 py-3.5 font-display text-sm font-bold text-slate-950 shadow-xl shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
            >
              {t("home.hero.cta.catalog")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-audio"
              onClick={() => document.getElementById("audiobooki")?.scrollIntoView({ behavior: "smooth" })}
              className="flex items-center gap-2 rounded-full glass-panel px-8 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
            >
              <Headphones className="h-4 w-4" /> {t("home.hero.cta.audio")}
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-4 flex flex-col items-center"
          >
            <button
              data-testid="hero-cta-film"
              onClick={() => document.getElementById("film")?.scrollIntoView({ behavior: "smooth", block: "center" })}
              className="hero-cta group rounded-full py-3 pl-6 pr-3 font-display text-sm font-bold text-white transition-transform hover:scale-105 active:scale-95 sm:pl-9 sm:pr-3.5"
            >
              <Sparkles className="relative z-10 h-4 w-4 shrink-0" />
              <span className="relative z-10 text-center">{t("home.hero.cta.film")}</span>
              <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-950/80 text-cyan-200">
                <ChevronsDown className="hero-cta-arrow h-4 w-4" />
              </span>
            </button>
            <span className="mt-2 text-[11px] font-semibold tracking-wide text-white [text-shadow:0_1px_12px_rgba(2,16,40,0.95)]">{t("home.hero.cta.film.hint")}</span>
          </motion.div>
        </div>
      </section>

      {/* JAK TO DZIAŁA (goście) / TWOJA BIBLIOTEKA (zalogowani z zakupami) */}
      <HomeLibraryStrip />

      {/* KATEGORIE + KATALOG */}
      <section id="katalog" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 sm:px-6">
        <Reveal>
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("home.categories.title.a")} <span className="gradient-text">{t("home.categories.title.b")}</span></h2>
          <p className="mt-2 text-sm text-slate-400">{t("home.categories.subtitle")}</p>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="category-bar">
            {[
              { key: "wszystkie", label: t("home.cat.all.label"), desc: `${products.filter((p) => p.format !== "aplikacja" && !p.id.startsWith("pakiet-")).length} ${t("home.cat.all.desc")}`, icon: Sparkles, g: "from-slate-500 to-slate-700", img: "/img/tiles/katalog.webp" },
              { key: "format:ebook", label: t("home.cat.ebooks.label"), desc: t("home.cat.ebooks.desc"), icon: BookOpen, g: "from-cyan-500 to-blue-600", img: "/img/tiles/ebooki.webp" },
              { key: "format:audiobook", label: t("home.cat.audiobooks.label"), desc: t("home.cat.audiobooks.desc"), icon: Headphones, g: "from-fuchsia-500 to-purple-600", img: "/img/tiles/audiobooki.webp" },
              { key: "section:aplikacje", label: t("home.cat.apps.label"), desc: t("home.cat.apps.desc"), icon: Smartphone, g: "from-teal-400 to-emerald-500", img: "/img/tiles/aplikacje.webp" },
              { key: "section:gry", label: t("home.cat.games.label"), desc: t("home.cat.games.desc"), icon: Gamepad2, g: "from-amber-400 to-orange-500", img: "/img/banners/digilib-world-campaign.jpeg" },
            ].map((f, fi) => {
              const Icon = f.icon;
              const isBookHub = f.key.startsWith("format:");
              const isSectionHub = f.key.startsWith("section:");
              const sectionKey = isSectionHub ? f.key.slice(8) : null;
              const isActive = isBookHub ? activeFormat === f.key.slice(7) : isSectionHub ? appGamesHub === sectionKey : active === f.key;
              return (
                <button
                  key={f.key}
                  data-testid={`category-filter-${f.key.replace(":", "-")}`}
                  onClick={() => {
                    if (isSectionHub) setAppGamesHub((h) => (h === sectionKey ? null : sectionKey));
                    else setActive(f.key);
                  }}
                  className={`on-image group relative flex items-center gap-2.5 overflow-hidden rounded-2xl glass-panel p-3 text-left sm:gap-3.5 sm:p-4 ${fi === 4 ? "col-span-2 sm:col-span-1" : ""} transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 ${isActive ? "border-cyan-400/60 shadow-[0_14px_40px_-12px_rgba(6,182,212,0.5)]" : "hover:border-cyan-400/40"}`}
                >
                  <img src={f.img} alt="" aria-hidden="true" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-55 transition-transform duration-500 group-hover:scale-110 group-hover:opacity-70" />
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20" />
                  <span className="tile-shine" />
                  <span className={`tile-icon relative z-10 flex h-9 w-9 shrink-0 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-gradient-to-br ${f.g} text-white shadow-lg transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110`} style={{ animationDelay: `${fi * 0.8}s` }}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="relative z-10 min-w-0">
                    <span className="block text-sm font-bold leading-tight text-white drop-shadow">{f.label}</span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-slate-300">{f.desc}</span>
                  </span>
                  {(isBookHub || isSectionHub) && (
                    <ChevronDown className={`relative z-10 ml-auto h-4 w-4 shrink-0 self-start sm:self-center text-slate-300 transition-transform duration-300 ${isActive ? "rotate-180 text-cyan-300" : ""}`} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Podkategorie — wysuwany, animowany panel, pokazuje TYLKO kategorie faktycznie obecne w wybranym formacie */}
          <AnimatePresence mode="wait">
            {activeFormat && (
              <motion.div
                key={activeFormat}
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: "auto", marginTop: 18 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                data-testid="subcategory-panel"
              >
                <div className="flex flex-wrap gap-2.5 rounded-2xl border border-cyan-500/10 bg-slate-900/30 p-4 backdrop-blur">
                  <button
                    data-testid={`category-filter-format-${activeFormat}-reset`}
                    onClick={() => setActive(`format:${activeFormat}`)}
                    className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${active === `format:${activeFormat}` ? "text-slate-950 shadow-lg bg-gradient-to-r from-slate-300 to-slate-100" : "glass-panel text-slate-300 hover:border-cyan-400/40"}`}
                  >
                    <Sparkles className="h-4 w-4" style={{ color: active === `format:${activeFormat}` ? undefined : "#94a3b8" }} /> Wszystkie
                  </button>
                  {bookCategoriesFor(activeFormat).map((c, ci) => {
                    const Icon = ICONS[c.icon] || BookOpen;
                    const col = CAT_COLORS[c.slug] || "#22d3ee";
                    const key = `fmtcat:${activeFormat}:${c.slug}`;
                    const isActive = active === key;
                    const countInFmt = products.filter((p) => p.format === activeFormat && p.category === c.slug).length;
                    return (
                      <motion.button
                        key={c.slug}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: ci * 0.04, duration: 0.25 }}
                        data-testid={`category-filter-${key.replace(/:/g, "-")}`}
                        onClick={() => setActive(key)}
                        className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${isActive ? "text-slate-950 shadow-lg" : "glass-panel text-slate-300 hover:border-cyan-400/40"}`}
                        style={isActive ? { background: `linear-gradient(90deg, ${col}, #22d3ee)` } : {}}
                      >
                        <Icon className="h-4 w-4" style={{ color: isActive ? undefined : col }} /> {c.name}
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${isActive ? "bg-slate-950/20" : "bg-slate-800"}`}>{countInFmt}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Aplikacje / Gry — ten sam wzorzec co Ebooki/Audiobooki: wysuwany panel z podkategoriami,
              tyle ze te dwie nie filtruja siatki na tej stronie, tylko przewijaja do wlasnej sekcji
              nizej i tam ustawiaja filtr (przez CustomEvent, ta sekcja ma wlasny stan podkategorii) */}
          <AnimatePresence mode="wait">
            {appGamesHub && (
              <motion.div
                key={appGamesHub}
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: "auto", marginTop: 18 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                data-testid="subcategory-panel-sections"
              >
                <div className="flex flex-wrap gap-2.5 rounded-2xl border border-cyan-500/10 bg-slate-900/30 p-4 backdrop-blur">
                  <button
                    data-testid={`category-filter-${appGamesHub}-all`}
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent(appGamesHub === "aplikacje" ? "set-app-subcategory" : "set-games-subject", { detail: "all" }));
                      document.getElementById(appGamesHub)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold glass-panel text-slate-300 transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-cyan-400/40"
                  >
                    <Sparkles className="h-4 w-4 text-slate-400" /> Wszystkie
                  </button>

                  {appGamesHub === "aplikacje" && APP_CATEGORIES.filter((c) => c.id !== "pakiety").map((c, ci) => {
                    const Icon = APP_CAT_ICONS[c.id] || Sparkles;
                    const count = products.filter((p) => AVAILABLE_APPS.find((m) => m.storeId === p.id)?.categories.includes(c.id)).length;
                    if (!count) return null;
                    return (
                      <motion.button
                        key={c.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: ci * 0.04, duration: 0.25 }}
                        data-testid={`category-filter-aplikacje-${c.id}`}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent("set-app-subcategory", { detail: c.id }));
                          document.getElementById("aplikacje")?.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold glass-panel text-slate-300 transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-fuchsia-400/40"
                      >
                        <Icon className="h-4 w-4 text-fuchsia-300" /> {c.label}
                        <span className="rounded-full bg-slate-800 px-1.5 py-0.5 text-[10px]">{count}</span>
                      </motion.button>
                    );
                  })}

                  {appGamesHub === "gry" && SUBJECTS.map((s, si) => {
                    const Icon = s.icon;
                    const count = games.filter((g) => g.id === `digilib-world-${s.slug}`).length;
                    if (!count) return null;
                    return (
                      <motion.button
                        key={s.slug}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: si * 0.04, duration: 0.25 }}
                        data-testid={`category-filter-gry-${s.slug}`}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent("set-games-subject", { detail: s.slug }));
                          document.getElementById("gry")?.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold glass-panel text-slate-300 transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-amber-400/40"
                      >
                        <Icon className="h-4 w-4 text-amber-300" /> {s.label}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
        <div key={active} className="mt-8">
          <ProductGrid products={filtered} testid="products-grid" />
        </div>
        {filtered.length === 0 && <p className="mt-10 text-center text-slate-500">{t("home.loadingCatalog")}</p>}

        {/* PAKIETY książkowe — pod produktami danej kategorii */}
        {(bundleEbooki || bundleAudio || bundleCombo) && (
          <div className="mt-12 grid gap-6 lg:grid-cols-2" data-testid="book-bundles">
            {bundleCombo && ["wszystkie", "format:ebook", "format:audiobook"].includes(active) && (
              <div className="lg:col-span-2">
                <BundleCard
                  testid="bundle-combo-card"
                  img="/img/tiles/ebook-audio.webp"
                  badge={<><Sparkles className="h-3.5 w-3.5" /> {t("home.bundles.combo.badge")}</>}
                  badgeCls="bg-gradient-to-r from-cyan-300 via-teal-300 to-emerald-300"
                  title={t("home.bundles.combo.title")}
                  desc={t("home.bundles.combo.desc")}
                  items={[`${nEbooks} ${t("home.bundles.combo.item1")}`, `${nAudio} ${t("home.bundles.combo.item2")}`, t("home.bundles.item.formats"), t("home.bundles.item.noSub")]}
                  product={bundleCombo}
                />
              </div>
            )}
            {bundleEbooki && ["wszystkie", "format:ebook"].includes(active) && (
              <BundleCard
                testid="bundle-ebooki-card"
                img="/img/tiles/ebooki.webp"
                badge={<><BookOpen className="h-3.5 w-3.5" /> {t("home.bundles.ebooki.badge")}</>}
                title={t("home.bundles.ebooki.title")}
                desc={t("home.bundles.ebooki.desc")}
                items={[`${nEbooks} ${t("home.bundles.ebooki.item")}`, t("home.bundles.ebooki.formats"), t("home.bundles.item.noSub")]}
                product={bundleEbooki}
              />
            )}
            {bundleAudio && ["wszystkie", "format:audiobook"].includes(active) && (
              <BundleCard
                testid="bundle-audio-card"
                img="/img/tiles/audiobooki.webp"
                badge={<><Headphones className="h-3.5 w-3.5" /> {t("home.bundles.audio.badge")}</>}
                title={t("home.bundles.audio.title")}
                desc={t("home.bundles.audio.desc")}
                items={[`${nAudio} ${t("home.bundles.audio.item")}`, t("home.bundles.audio.freeSamples"), t("home.bundles.item.noSub")]}
                product={bundleAudio}
              />
            )}
          </div>
        )}
      </section>

      {/* BANERY */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <BannerCarousel />
        </Reveal>
      </section>

      {/* LEKTURY SZKOLNE — zestawy (lektura + opracowanie + audiobooki + aplikacja); sekcja znika, gdy brak pozycji */}
      {lektury.length > 0 && (
        <section id="lektury-szkolne" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 sm:px-6" data-testid="home-lektury">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-300">
              <GraduationCap className="h-3.5 w-3.5" /> {t("home.lektury.badge")}
            </span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("home.lektury.title.a")} <span className="gradient-text">{t("home.lektury.title.b")}</span></h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("home.lektury.subtitle")}</p>
          </Reveal>
          <div className="mt-8">
            <ProductGrid products={lektury} testid="lektury-grid" />
          </div>
        </section>
      )}

      {/* BESTSELLERY */}
      <section id="bestsellery" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 sm:px-6">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("home.bestsellers.title.a")} <span className="gradient-text">{t("home.bestsellers.title.b")}</span></h2>
              <p className="mt-2 text-sm text-slate-400">{t("home.bestsellers.subtitle")}</p>
            </div>
          </div>
        </Reveal>
        <div className="mt-8">
          <ProductGrid products={bestsellers} testid="bestsellers-grid" />
        </div>
      </section>

      {/* BANER PROMO: LATO */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <PromoStrip
            testid="promo-strip-lato"
            img="/img/hero/promo-lato.jpg"
            badge={t("home.promoLato.badge")}
            title={t("home.promoLato.title")}
            subtitle={t("home.promoLato.subtitle")}
            cta={t("home.promoLato.cta")}
            target="katalog"
            accent="from-amber-400 to-orange-500"
          />
        </Reveal>
      </section>

      {/* AUDIOBOOKI */}
      <section id="audiobooki" className="relative scroll-mt-24 overflow-hidden py-20">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(6,182,212,0.08), transparent 70%)" }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-teal-500/10 border border-teal-500/30 px-4 py-1.5 text-xs font-semibold text-teal-300">
                  <Headphones className="h-3.5 w-3.5" /> {t("home.audio.badge")}
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                  {t("home.audio.title.a")} <span className="gradient-text">{t("home.audio.title.b")}</span>
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
                  {t("home.audio.subtitle")}
                </p>
                <div className="mt-8">
                  <WaveformPlayer />
                </div>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
              {audiobooks.map((p, i) => (
                <Reveal key={p.id} delay={i * 100}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEKCJA APLIKACJI INTERAKTYWNYCH — po strefie książkowej */}
      <PromoVideo />
      <TutorCampaign />
      <AppsSection apps={apps} />

      {/* GRY EDUKACYJNE — osobna kategoria */}
      <GamesSection games={games} />
      <RaceSection games={games} />

      {/* STATYSTYKI */}
      <section className="border-y border-cyan-500/10 bg-slate-900/40 py-16">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-10 px-4 sm:px-6 lg:grid-cols-4" data-testid="stats-section">
          <StatCounter key={`t${facts?.titles}`} value={facts?.titles || 0} label={t("home.stats.titles")} />
          <StatCounter key={`h${facts?.audio_hours}`} value={facts?.audio_hours || 0} suffix=" h" label={t("home.stats.audioHours")} />
          <StatCounter key={`a${facts?.apps}`} value={facts?.apps || 0} label={t("home.stats.apps")} />
          <StatCounter value={5} label={t("home.stats.languages")} />
        </div>
      </section>

      {/* BANER PROMO: ROZWÓJ */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <PromoStrip
            testid="promo-strip-rozwoj"
            img="/img/hero/promo-rozwoj.jpg"
            badge={t("home.promoRozwoj.badge")}
            title={t("home.promoRozwoj.title")}
            subtitle={t("home.promoRozwoj.subtitle")}
            cta={t("home.promoRozwoj.cta")}
            target="audiobooki"
            accent="from-cyan-400 to-blue-500"
          />
        </Reveal>
      </section>

      {/* QUIZ */}
      <section id="quiz" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6">
        <Reveal>
          <QuizSection />
        </Reveal>
      </section>

      {/* KAMPANIA PAKIETÓW */}
      <PricingCampaign />

      {/* WIDGET: ZGŁOŚ KSIĄŻKĘ */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <div className="glow-border rounded-3xl p-[2px]" data-testid="book-request-widget">
            <div className="relative overflow-hidden rounded-3xl bg-slate-950/95 px-6 py-12 text-center sm:px-12">
              <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full bg-fuchsia-500/15 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-cyan-500/15 blur-3xl" />
              <div className="relative">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-fuchsia-500 to-cyan-500 shadow-lg shadow-fuchsia-500/30">
                  <MailQuestion className="h-7 w-7 text-white" />
                </div>
                <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
                  {t("home.bookRequest.title.a")} <span className="gradient-text">{t("home.bookRequest.title.b")}</span>
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
                  {t("home.bookRequest.text")}
                </p>
                <button
                  data-testid="book-request-widget-cta"
                  onClick={() => window.dispatchEvent(new Event("open-book-request"))}
                  className="mt-8 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 px-9 py-3.5 font-display text-sm font-bold text-white shadow-xl shadow-fuchsia-500/30 transition-transform hover:scale-105 active:scale-95"
                >
                  {t("home.bookRequest.cta")}
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* OPINIE */}
      <section className="mx-auto max-w-7xl px-4 pb-24 pt-8 sm:px-6">
        <Reveal>
          <h2 className="text-center font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("home.testimonials.title.a")} <span className="gradient-text">{t("home.testimonials.title.b")}</span></h2>
        </Reveal>
        <Reveal delay={120}>
          <div className="mt-12">
            <Testimonials />
          </div>
        </Reveal>
      </section>

      {/* NEWSLETTER + KOD RABATOWY -5% */}
      <NewsletterSection />
    </main>
  );
}
